import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { hasPlatformAuthenticator, registerPasskey } from "@/lib/webauthn";
import { Fingerprint, ShieldCheck, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const DISMISS_SESSION_KEY = "ht_passkey_prompt_dismissed_session";

/** Detection best-effort di dispositivo mobile / touch. */
function isMobileLike() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(pointer: coarse)")?.matches) return true;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
}

/**
 * Modal che invita l'utente ad abilitare la Passkey (Face ID / Touch ID / Impronta / PIN device).
 * Appare in due punti:
 *   1. Dopo il completamento onboarding (`trigger="onboarding"`) → sempre se mobile+supportato
 *   2. Dopo il login (`trigger="login"`) → solo se l'utente non ha ancora dismisso permanentemente
 *
 * L'auto-check evita di mostrarlo se:
 *   - non siamo su mobile (pointer coarse / mobile UA)
 *   - il device non supporta un platform authenticator
 *   - l'utente ha già una passkey registrata
 *   - trigger=login E user.passkey_prompt_dismissed=true
 *   - trigger=login E l'utente ha già cliccato "Non ora" in questa sessione browser
 */
export default function PasskeyEnrollModal({ user, trigger = "login", onClose }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user) return;
      // Non proporre la passkey in modalità demo: gli utenti pubblici che entrano
      // dalla landing per esplorare non hanno un vero account da proteggere.
      try { if (localStorage.getItem("ht_demo_mode") === "1") return; } catch {}
      if (!isMobileLike()) return;
      // Se user dice "non chiedere più" e siamo in post-login → skip
      if (trigger === "login" && user.passkey_prompt_dismissed) return;
      // Se in questa sessione ha già cliccato "Non ora" → skip
      if (trigger === "login" && sessionStorage.getItem(DISMISS_SESSION_KEY) === "1") return;
      const supported = await hasPlatformAuthenticator();
      if (!supported) return;
      // Se ha già almeno una passkey → skip
      try {
        const r = await api.get("/auth/webauthn/credentials");
        if (Array.isArray(r.data) && r.data.length > 0) return;
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("[passkey-modal] cred list:", e);
        return;
      }
      if (!cancelled) setOpen(true);
    };
    check();
    return () => { cancelled = true; };
  }, [user, trigger]);

  const dismiss = async (permanent) => {
    setBusy(true);
    try {
      if (permanent) {
        await api.post("/auth/passkey-prompt/dismiss", { permanent: true });
      } else {
        sessionStorage.setItem(DISMISS_SESSION_KEY, "1");
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[passkey-modal] dismiss:", e);
    } finally {
      setBusy(false);
      setOpen(false);
      onClose?.();
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const label = navigator.userAgentData?.platform
        || (/iPhone|iPad|iPod/i.test(navigator.userAgent) ? "iPhone/iPad"
          : /Android/i.test(navigator.userAgent) ? "Android"
          : "Questo dispositivo");
      await registerPasskey(label);
      try { localStorage.setItem("ht_passkey_last_used", "1"); } catch {}
      toast.success("Passkey attivata. La prossima volta accedi con impronta/Face ID.");
      setOpen(false);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Non è stato possibile attivare la passkey";
      // Se l'utente annulla dal prompt OS non è un errore vero
      if (err?.name === "NotAllowedError" || /cancell|abort/i.test(msg)) {
        toast("Attivazione annullata. Puoi farlo più tardi dalle Impostazioni → Sicurezza.");
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="passkey-enroll-modal"
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-8 relative">
        <button
          onClick={() => dismiss(false)}
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-black/60"
          data-testid="passkey-modal-close"
          aria-label="Chiudi"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-xl shadow-purple-500/30 mb-4">
            <Fingerprint size={44} weight="duotone" color="white" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-black" data-testid="passkey-modal-title">
            Accedi più velocemente
          </h2>
          <p className="text-sm text-black/60 mt-2 max-w-sm">
            Attiva l'accesso con <strong>impronta digitale</strong>, <strong>Face ID</strong> o il <strong>PIN del tuo dispositivo</strong>.
            Basta un tocco, senza digitare la password.
          </p>
        </div>

        <div className="mt-6 rounded-2xl bg-purple-50/60 border border-purple-100 p-4 flex items-start gap-3">
          <ShieldCheck size={22} weight="duotone" className="text-purple-700 shrink-0 mt-0.5" />
          <div className="text-xs text-black/70 leading-relaxed">
            <strong className="text-black">Sicuro e privato.</strong>{" "}
            La chiave non lascia mai il tuo dispositivo. Nessun codice condiviso, protezione contro il phishing.
          </div>
        </div>

        <div className="mt-6 space-y-2">
          <button
            onClick={enable}
            disabled={busy}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm shadow-lg shadow-purple-500/30 hover:opacity-95 active:scale-[.98] transition-all disabled:opacity-60"
            data-testid="passkey-modal-enable"
          >
            {busy ? "Attivazione…" : "Attiva ora"}
          </button>
          <button
            onClick={() => dismiss(false)}
            disabled={busy}
            className="w-full h-11 rounded-xl border border-black/10 bg-white text-black/70 font-medium text-sm hover:bg-black/5 active:scale-[.98] transition-all"
            data-testid="passkey-modal-later"
          >
            Non ora
          </button>
          <button
            onClick={() => dismiss(true)}
            disabled={busy}
            className="w-full text-xs text-black/40 hover:text-black/70 py-2 hover:underline"
            data-testid="passkey-modal-never"
          >
            Non chiedere più
          </button>
        </div>
      </div>
    </div>
  );
}
